import { useState } from "react";
import {
  CopyButton,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ArrheniusStrings,
  arrheniusMessages,
  DEFAULT_SIG,
  ENERGY_OPTIONS,
  eaText,
  R_TEXT,
  SolvedCell,
  TEMPERATURE_OPTIONS,
} from "@/components/tools/chem-physical/arrhenius-shared";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ARRHENIUS_TERMS,
  type ArrheniusSolution,
  type ArrheniusTerm,
  solveArrhenius,
} from "@/lib/tool-kit/chem-physical/arrhenius";
import { MOLAR_ENERGY_UNITS } from "@/lib/tool-kit/chem-physical/gibbs";
import { unitFactor, unitSymbol } from "@/lib/tool-kit/chem-physical/heat";
import { temperatureSymbol } from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

/* ------------------------------------------------- k = A e^(−Ea/RT) */

function EquationAnswer({
  solution,
  texts,
  units,
  kUnit,
  sig,
  strings,
}: {
  solution: ArrheniusSolution;
  texts: Record<ArrheniusTerm, string>;
  units: { ea: string; t: string };
  kUnit: string;
  sig: number;
  strings: ArrheniusStrings;
}) {
  const fmt = useToolNumber();
  const { unknown } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const labels: Record<ArrheniusTerm, string> = {
    k: strings.termK,
    a: strings.termA,
    ea: strings.termEa,
    t: strings.termT,
  };
  const withUnit = (value: number) =>
    `${formatSig(fmt, value, sig)} ${kUnit}`.trim();
  const answerText = {
    k: withUnit(solution.k),
    a: withUnit(solution.a),
    ea: eaText(fmt, solution.ea, units.ea, sig),
    t: `${formatSig(fmt, solution.answer, sig)} ${temperatureSymbol(units.t)}`,
  }[unknown];
  const conversions: string[] = [];
  if (unknown !== "ea" && unitFactor(MOLAR_ENERGY_UNITS, units.ea) !== 1) {
    conversions.push(
      t(strings.convertLine, {
        label: labels.ea,
        value: texts.ea.trim(),
        unit: unitSymbol(MOLAR_ENERGY_UNITS, units.ea),
        converted: `${p(solution.ea)} J/mol`,
      })
    );
  }
  if (unknown !== "t" && units.t !== "K") {
    conversions.push(
      t(strings.convertLine, {
        label: labels.t,
        value: texts.t.trim(),
        unit: temperatureSymbol(units.t),
        converted: `${p(solution.kelvin)} K`,
      })
    );
  }
  const substituted = t(
    {
      k: strings.substitutedK,
      a: strings.substitutedA,
      ea: strings.substitutedEa,
      t: strings.substitutedT,
    }[unknown],
    {
      k: p(solution.k),
      a: p(solution.a),
      ea: p(solution.ea),
      t: p(solution.kelvin),
      r: R_TEXT,
      exp: p(solution.exponent),
      result: {
        k: formatSig(fmt, solution.k, sig + 1),
        a: formatSig(fmt, solution.a, sig + 1),
        ea: `${formatSig(fmt, solution.ea, sig + 1)} J/mol`,
        t: `${formatSig(fmt, solution.kelvin, sig + 1)} K`,
      }[unknown],
    }
  );
  const rearranged = {
    k: strings.equation,
    a: strings.rearrangedA,
    ea: strings.rearrangedEa,
    t: strings.rearrangedT,
  }[unknown];
  const boltzmann = Math.exp(-solution.exponent);
  const needsConversion =
    (unknown === "ea" && unitFactor(MOLAR_ENERGY_UNITS, units.ea) !== 1) ||
    (unknown === "t" && units.t !== "K");
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard
          label={strings.exponent}
          value={formatSig(fmt, solution.exponent, sig)}
        />
        <StatCard
          label={strings.fractionEnergetic}
          value={formatSig(fmt, boltzmann, sig)}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        {unknown === "k" ? null : <p>{rearranged}</p>}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, {
              result:
                unknown === "ea"
                  ? `${formatSig(fmt, solution.ea, sig + 1)} J/mol`
                  : `${formatSig(fmt, solution.kelvin, sig + 1)} K`,
              answer: answerText,
            })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function EquationPanel({
  strings,
  sigChoice,
}: {
  strings: ArrheniusStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<ArrheniusTerm>("k");
  const [text, setText] = useState<Record<ArrheniusTerm, string>>({
    k: "",
    a: "1.00e13",
    ea: "75.0",
    t: "25.0",
  });
  const [unit, setUnit] = useState({ ea: "kJ", t: "C" });
  const [kUnit, setKUnit] = useState("s⁻¹");
  const outcome = solveArrhenius({ unknown, text, unit });
  const typed = ARRHENIUS_TERMS.filter((term) => term !== unknown).map(
    (term) => text[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<ArrheniusTerm, string> = {
    k: strings.termK,
    a: strings.termA,
    ea: strings.termEa,
    t: strings.termT,
  };
  const label = (term: ArrheniusTerm) =>
    unknown === term
      ? t(strings.answerSuffix, { label: labels[term] })
      : labels[term];
  const rateField = (term: "k" | "a") =>
    unknown === term ? (
      <SolvedCell
        id={`arr-${term}`}
        key={term}
        label={label(term)}
        text={strings.solvedCell}
      />
    ) : (
      <TextField
        hint={term === "a" ? strings.aHint : strings.kHint}
        id={`arr-${term}`}
        key={term}
        label={label(term)}
        numeric
        onChange={(value) => setText({ ...text, [term]: value })}
        value={text[term]}
      />
    );
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="arr-solve"
        onChange={setUnknown}
        options={ARRHENIUS_TERMS.map((term) => ({
          value: term,
          label: labels[term],
        }))}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {rateField("k")}
        {rateField("a")}
        <QuantityInput
          id="arr-ea"
          label={label("ea")}
          onUnitChange={(value) => setUnit({ ...unit, ea: value })}
          onValueChange={(value) => setText({ ...text, ea: value })}
          solved={unknown === "ea"}
          unit={unit.ea}
          unitLabel={strings.unitEa}
          units={ENERGY_OPTIONS}
          value={text.ea}
        />
        <QuantityInput
          id="arr-t"
          label={label("t")}
          onUnitChange={(value) => setUnit({ ...unit, t: value })}
          onValueChange={(value) => setText({ ...text, t: value })}
          solved={unknown === "t"}
          unit={unit.t}
          unitLabel={strings.unitTemperature}
          units={TEMPERATURE_OPTIONS}
          value={text.t}
        />
        <TextField
          hint={strings.kUnitHint}
          id="arr-kunit"
          label={strings.kUnit}
          onChange={setKUnit}
          value={kUnit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <EquationAnswer
            kUnit={kUnit.trim()}
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={arrheniusMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
